/**
 * F-005 — Central gravity well.
 *
 * Replaces world-vector gravity (intentionally zero in F-004's PhysicsWorld)
 * with a radial inverse-distance field. Bodies inside ``maxRadius`` get a
 * force pulling toward ``center``; outside, the well is silent.
 *
 * Force model: F = strength · mass / max(distance, minDistance)^2 along the
 * unit vector toward center. The ``minDistance`` clamp prevents singularity
 * spikes when a body brushes the well core.
 */

import { Vec3, type Body } from "cannon-es";

const DEFAULT_STRENGTH = 18;
const DEFAULT_MAX_RADIUS = 30;
const DEFAULT_MIN_DISTANCE = 0.4;

export interface GravityWellOptions {
  center?: { x: number; y: number; z: number };
  strength?: number;
  maxRadius?: number;
  minDistance?: number;
}

export interface GravityWell {
  center: Vec3;
  strength: number;
  maxRadius: number;
  minDistance: number;
  applyTo(body: Body): void;
  applyToAll(bodies: ReadonlyArray<Body>): void;
}

export function createGravityWell(opts: GravityWellOptions = {}): GravityWell {
  const center = new Vec3(
    opts.center?.x ?? 0,
    opts.center?.y ?? 0,
    opts.center?.z ?? 0,
  );
  const strength = opts.strength ?? DEFAULT_STRENGTH;
  const maxRadius = opts.maxRadius ?? DEFAULT_MAX_RADIUS;
  const minDistance = opts.minDistance ?? DEFAULT_MIN_DISTANCE;

  if (strength <= 0) {
    throw new Error("gravity well: strength must be positive");
  }
  if (maxRadius <= 0) {
    throw new Error("gravity well: maxRadius must be positive");
  }
  if (minDistance <= 0) {
    throw new Error("gravity well: minDistance must be positive");
  }

  const applyTo = (body: Body): void => {
    if (body.mass === 0) return; // static / kinematic — no pull.
    const dx = center.x - body.position.x;
    const dy = center.y - body.position.y;
    const dz = center.z - body.position.z;
    const distSq = dx * dx + dy * dy + dz * dz;
    if (distSq > maxRadius * maxRadius) return;
    const dist = Math.max(Math.sqrt(distSq), minDistance);
    const magnitude = (strength * body.mass) / (dist * dist);
    const inv = 1 / dist;
    body.applyForce(
      new Vec3(dx * inv * magnitude, dy * inv * magnitude, dz * inv * magnitude),
      body.position,
    );
  };

  const applyToAll = (bodies: ReadonlyArray<Body>): void => {
    for (const b of bodies) applyTo(b);
  };

  return { center, strength, maxRadius, minDistance, applyTo, applyToAll };
}

export const __testing = {
  DEFAULT_STRENGTH,
  DEFAULT_MAX_RADIUS,
  DEFAULT_MIN_DISTANCE,
};
